// Gear for the skier. Basic gear is free; the rest is bought with buns a
// signed-in player has collected over all their runs. The wallet is one doc
// per uid in skiWallet, see firestore.rules.
import firebase from "firebase/app";
import { db } from "../../FIREBASE/firebase";
import { Look, DEFAULT_LOOK } from "./game";

export interface Item { value: string; label?: string; price: number }
export interface Pick { key: keyof Look; label: string; items: Item[] }

const col = (value: string, price = 0): Item => ({ value, price });
const opt = (value: string, label: string, price = 0): Item => ({ value, label, price });
const kit = (cheap: number) => [
  col("#e2b31a"), col("#2f6fd6"), col("#d63a2f"), col("#1d1f24"),
  col("#f4f1ea", cheap), col("#2f9e5a", cheap), col("#f08a24", cheap), col("#e05ca8", cheap),
  col("#c9a227", cheap * 5), // gold
];

export const SHOP: [string, Pick[]][] = [
  ["Kläder", [
    { key: "jersey", label: "Tröja", items: kit(20) },
    { key: "pants", label: "Byxor", items: [col("#23324f"), col("#1d1f24"), col("#5a5f69"), col("#2f6fd6", 15), col("#6b3e2a", 15), col("#d63a2f", 15)] },
    { key: "skin", label: "Hy", items: [col("#f6d7bc"), col("#f1c9a5"), col("#d9a77c"), col("#b07a52"), col("#7a4e30")] },
    { key: "head", label: "Huvud", items: [opt("helmet", "Hjälm"), opt("beanie", "Mössa", 15), opt("cap", "Keps", 15), opt("band", "Pannband", 25)] },
    { key: "headColor", label: "Färg", items: kit(10) },
  ]],
  ["Utrustning", [
    { key: "skis", label: "Skidor", items: [col("#1f2a44"), col("#d63a2f", 25), col("#f4f1ea", 25), col("#2f9e5a", 25), col("#c9a227", 150)] },
    { key: "poles", label: "Stavar", items: [col("#c9ced6"), col("#1d1f24", 15), col("#d63a2f", 15), col("#2f6fd6", 15), col("#c9a227", 80)] },
    { key: "glasses", label: "Glasögon", items: [opt("black", "Svarta"), opt("none", "Inga"), opt("mirror", "Spegel", 40)] },
    { key: "pack", label: "Vätska", items: [opt("none", "Inget"), opt("belt", "Bälte", 30), opt("vest", "Väst", 60)] },
  ]],
];

export const itemId = (key: keyof Look, value: string) => `${key}:${value}`;

export const priceOf = (key: keyof Look, value: string): number => {
  for (const [, picks] of SHOP) for (const p of picks) if (p.key === key) return p.items.find((i) => i.value === value)?.price ?? 0;
  return 0;
};

export interface Wallet { earned: number; spent: number; owned: string[] }
export const balance = (w: Wallet) => w.earned - w.spent;

export const canWear = (w: Wallet | null, key: keyof Look, value: string) =>
  priceOf(key, value) === 0 || !!w?.owned.includes(itemId(key, value));

// Anything not owned (other device, signed out) falls back to the free default
export const wearable = (look: Look, w: Wallet | null): Look => {
  const out = { ...look };
  (Object.keys(out) as (keyof Look)[]).forEach((k) => {
    if (!canWear(w, k, String(out[k]))) (out as Record<string, unknown>)[k] = DEFAULT_LOOK[k];
  });
  return out;
};

const wallets = () => db.collection("skiWallet");
const read = (d: firebase.firestore.DocumentSnapshot): Wallet => ({
  earned: Number(d.get("earned") ?? 0),
  spent: Number(d.get("spent") ?? 0),
  owned: (d.get("owned") as string[] | undefined) ?? [],
});

export async function loadWallet(uid: string): Promise<Wallet> {
  return read(await wallets().doc(uid).get());
}

// Adds a finished run's buns to the player's wallet
export async function earnBuns(uid: string, buns: number): Promise<Wallet> {
  const ref = wallets().doc(uid);
  return db.runTransaction(async (tx) => {
    const w = read(await tx.get(ref));
    const next = { ...w, earned: w.earned + Math.floor(buns) };
    tx.set(ref, { ...next, at: firebase.firestore.FieldValue.serverTimestamp() });
    return next;
  });
}

// Buys one item; fails if it is already owned or there are too few buns
export async function buyItem(uid: string, key: keyof Look, value: string): Promise<Wallet> {
  const ref = wallets().doc(uid);
  const id = itemId(key, value);
  const price = priceOf(key, value);
  return db.runTransaction(async (tx) => {
    const w = read(await tx.get(ref));
    if (w.owned.includes(id) || balance(w) < price) throw new Error("cannot buy");
    const next = { ...w, spent: w.spent + price, owned: [...w.owned, id] };
    tx.set(ref, { ...next, at: firebase.firestore.FieldValue.serverTimestamp() });
    return next;
  });
}
